<template>
    <div class="rounded-lgr h-full p-6 new-home-card border-2 border-valence-grey-250" :style="{ ...backgroundStyles }">
        <div class="flex flex-col gap-4 relative h-full" :class="{ 'rounded-lgr p-6 bg-[#fffaf699] backdrop-blur-[20px] ': backdrop }">
            <div v-if="icon" class="pb-6" aria-hidden="true">
                <component :is="icon" />
            </div>
            <div v-if="$slots.header"><slot name="header"></slot></div>
            <div>
                <slot name="subtitle">
                    <div class="body-small text-valence-grey-750">{{ subtitle }}</div>
                </slot>
                <slot name="title">
                    <div class="title-body wrap-break-word text-valence-grey-850">{{ title }}</div>
                </slot>
            </div>
            <div v-if="content">
                <p ref="contentRef" class="body fs-mask text-valence-grey-750" :class="{ 'line-clamp-4': !expanded }">
                    {{ content }}
                </p>
                <button
                    v-if="isTruncated"
                    type="button"
                    class="body-small text-valence-grey-850 underline mt-1 cursor-pointer"
                    :aria-expanded="expanded"
                    @click="expanded = !expanded">
                    {{ expanded ? "Less" : "More" }}
                </button>
            </div>
            <slot v-if="$slots.content" name="content"></slot>
            <div v-if="ctaButtonText || $slots.cta" class="mt-auto">
                <slot name="cta">
                    <BaseButton v-if="ctaButtonText" theme="neutral" class="w-full" :style="ctaButtonStyle" @click="$emit('cta-click')">
                        {{ ctaButtonText }}
                    </BaseButton>
                </slot>
            </div>
            <slot name="footer"></slot>
        </div>
    </div>
</template>

<script setup>
import BaseButton from "~vue/components/BaseButton.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

const { cardType, backgroundStyle, title, content } = defineProps({
    cardType: { type: String, required: true },
    title: { type: String, default: undefined },
    subtitle: { type: String, default: undefined },
    content: { type: String, default: undefined },
    ctaButtonText: { type: String, default: undefined },
    backgroundStyle: { type: String, default: undefined },
    backdrop: { type: Boolean, default: false }, // Whether to show the backdrop
    ctaButtonStyle: { type: Object, default: () => ({}) },
    icon: { type: [Object, Function, String], default: null }, // Accepts component or path
});

defineEmits(["cta-click"]);

const backgroundStyles = computed(() => {
    if (backgroundStyle) {
        return backgroundStyle;
    }
    return { backgroundColor: "white" };
});

const contentRef = ref(null);
const expanded = ref(false);
const isTruncated = ref(false);
let resizeObserver = null;

const measureTruncation = () => {
    if (!contentRef.value || expanded.value) {
        return;
    }
    const el = contentRef.value;
    isTruncated.value = el.scrollHeight > el.clientHeight + 1;
};

onMounted(async () => {
    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CARD_SHOWN, { card_type: cardType, title });
    await nextTick();
    measureTruncation();
    if (typeof ResizeObserver !== "undefined" && contentRef.value) {
        resizeObserver = new ResizeObserver(() => measureTruncation());
        resizeObserver.observe(contentRef.value);
    }
});

onUnmounted(() => {
    if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
    }
});

watch(
    () => content,
    async () => {
        await nextTick();
        measureTruncation();
    },
);
</script>
